import { useState } from "react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  ventasPorDia,
  ventasPorTipoEntrada,
  ventasPorFechaFuncion,
} from "../../../utils/BoleteriaUtils";
import PredictiveStats from "./PredictiveStats";

const EstadisticasBoleteria = ({ reportes, evento }) => {
  const [vistaActiva, setVistaActiva] = useState("prediccion");

  if (!reportes || !reportes.data || reportes.data.length === 0) {
    return (
      <div className="container-fluid my-4">
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <i className="bi bi-graph-up text-muted" style={{ fontSize: "3rem" }}></i>
            <p className="text-muted mt-3">No hay datos suficientes para mostrar estadísticas</p>
          </div>
        </div>
      </div>
    );
  }

  const dataPorDia = ventasPorDia(reportes);
  const dataPorTipo = ventasPorTipoEntrada(reportes);
  const dataPorFuncion = ventasPorFechaFuncion(reportes);

  // Colores para los gráficos de pie
  const COLORS = ["#d0552a", "#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d"];

  // Formatear moneda
  const formatCurrency = (value) => {
    return `$${parseFloat(value).toLocaleString("es-AR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border shadow-sm p-3 rounded">
          <p className="fw-bold mb-1">{label}</p>
          <p className="text-success mb-0">
            <small>Total: {formatCurrency(payload[0].value)}</small>
          </p>
          {payload[0].payload.cantidad && (
            <p className="text-muted mb-0">
              <small>Entradas: {payload[0].payload.cantidad}</small>
            </p>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="container-fluid my-4">
      <div className="card shadow-sm">
        <div className="card-body">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h5 className="mb-0 fw-bold d-flex align-items-center">
              <i className="bi bi-graph-up-arrow me-2 text-primary" style={{ fontSize: "1.5rem" }}></i>
              Estadísticas de Ventas
            </h5>

            {/* Selector de vista */}
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${vistaActiva === "prediccion" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setVistaActiva("prediccion")}
              >
                <i className="bi bi-graph-up-arrow me-1"></i>
                Predicciones
              </button>
              <button
                type="button"
                className={`btn btn-sm ${vistaActiva === "dia" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setVistaActiva("dia")}
              >
                <i className="bi bi-calendar-date me-1"></i>
                Por día de compra
              </button>
              <button
                type="button"
                className={`btn btn-sm ${vistaActiva === "funcion" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setVistaActiva("funcion")}
              >
                <i className="bi bi-calendar-event me-1"></i>
                Por función
              </button>
              <button
                type="button"
                className={`btn btn-sm ${vistaActiva === "tipo" ? "btn-primary" : "btn-outline-primary"}`}
                onClick={() => setVistaActiva("tipo")}
              >
                <i className="bi bi-ticket-perforated me-1"></i>
                Por tipo de entrada
              </button>
            </div>
          </div>

          <div className="row">
            {/* Vista de Predicciones */}
            {vistaActiva === "prediccion" && (
              <div className="col-12">
                <PredictiveStats reportes={reportes} evento={evento} />
              </div>
            )}

            {/* Gráfico de líneas - Ventas por día */}
            {vistaActiva === "dia" && (
              <div className="col-12">
                <div className="mb-3">
                  <h6 className="text-muted fw-bold">
                    <i className="bi bi-graph-up me-2"></i>
                    Evolución de ventas por día de compra
                  </h6>
                  <small className="text-muted">
                    Muestra el total de ingresos generados agrupados por la fecha en que se realizó la compra
                  </small>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={dataPorDia}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="fecha"
                      tick={{ fontSize: 12 }}
                      angle={-45}
                      textAnchor="end"
                      height={80}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="total"
                      stroke="#d0552a"
                      strokeWidth={3}
                      name="Ingresos"
                      dot={{ fill: "#d0552a", r: 5 }}
                      activeDot={{ r: 8 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Gráfico de barras - Ventas por fecha de función */}
            {vistaActiva === "funcion" && (
              <div className="col-12">
                <div className="mb-3">
                  <h6 className="text-muted fw-bold">
                    <i className="bi bi-bar-chart me-2"></i>
                    Ingresos por fecha de función
                  </h6>
                  <small className="text-muted">
                    Muestra el total de ingresos agrupados por la fecha y hora de la función programada
                  </small>
                </div>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={dataPorFuncion}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="fecha"
                      tick={{ fontSize: 11 }}
                      angle={-45}
                      textAnchor="end"
                      height={100}
                    />
                    <YAxis
                      tick={{ fontSize: 12 }}
                      tickFormatter={(value) => `$${value.toLocaleString()}`}
                    />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Bar dataKey="total" fill="#d0552a" name="Ingresos" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Gráfico de pie - Distribución por tipo de entrada */}
            {vistaActiva === "tipo" && (
              <div className="col-12">
                <div className="row">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <h6 className="text-muted fw-bold">
                        <i className="bi bi-pie-chart me-2"></i>
                        Distribución de ingresos por tipo de entrada
                      </h6>
                      <small className="text-muted">
                        Proporción de ingresos según el tipo de entrada vendida
                      </small>
                    </div>
                    <ResponsiveContainer width="100%" height={400}>
                      <PieChart>
                        <Pie
                          data={dataPorTipo}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: ${formatCurrency(entry.value)}`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {dataPorTipo.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip
                          formatter={(value) => formatCurrency(value)}
                          contentStyle={{ borderRadius: "8px" }}
                        />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>

                  <div className="col-md-6">
                    <div className="mb-3">
                      <h6 className="text-muted fw-bold">
                        <i className="bi bi-list-ol me-2"></i>
                        Detalle por tipo de entrada
                      </h6>
                    </div>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Tipo de Entrada</th>
                            <th className="text-end">Cantidad</th>
                            <th className="text-end">Total</th>
                            <th className="text-center">%</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dataPorTipo.map((tipo, index) => {
                            const totalGeneral = dataPorTipo.reduce((sum, t) => sum + t.value, 0);
                            const porcentaje = ((tipo.value / totalGeneral) * 100).toFixed(1);
                            return (
                              <tr key={index}>
                                <td>
                                  <span
                                    className="d-inline-block me-2"
                                    style={{
                                      width: "12px",
                                      height: "12px",
                                      backgroundColor: COLORS[index % COLORS.length],
                                      borderRadius: "2px",
                                    }}
                                  ></span>
                                  {tipo.name}
                                </td>
                                <td className="text-end">{tipo.cantidad}</td>
                                <td className="text-end fw-semibold text-success">
                                  {formatCurrency(tipo.value)}
                                </td>
                                <td className="text-center">
                                  <span className="badge bg-light text-dark">{porcentaje}%</span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EstadisticasBoleteria;
